import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const MenuContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const PhoneText = styled.span`
    color: ${({ theme }) => theme?.colors?.primary?.main || '#667eea'};
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;

    &:hover {
        background: ${({ theme }) => theme?.colors?.primary?.light || '#667eea'}15;
        text-decoration: underline;
    }
`;

const Menu = styled.div`
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 160px;
    overflow: hidden;
    animation: slideDown 0.2s ease-out;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid white;
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateX(-50%) translateY(-5px);
        }
        to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
    }
`;

const MenuItem = styled.button`
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: white;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[700] || '#2d3748'};
    transition: background 0.15s;

    &:hover {
        background: ${({ theme }) => theme?.colors?.gray?.[50] || '#f7fafc'};
    }

    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e2e8f0'};
    }

    svg {
        width: 18px;
        height: 18px;
        color: ${({ theme }) => theme?.colors?.primary?.main || '#667eea'};
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
`;

const CopyIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
);

const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
);

export default function PhoneActionMenu({ phoneNumber }) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            const handleEscape = (e) => {
                if (e.key === 'Escape') {
                    setIsOpen(false);
                }
            };
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen]);

    const handleCopy = async () => {
        try {
            const cleanPhone = phoneNumber.replace(/\D/g, '');
            await navigator.clipboard.writeText(cleanPhone);
            setIsOpen(false);
        } catch (error) {
            // Fallback para navegadores mais antigos
            const textArea = document.createElement('textarea');
            textArea.value = phoneNumber.replace(/\D/g, '');
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                // Silently fail
            }
            document.body.removeChild(textArea);
            setIsOpen(false);
        }
    };

    const handleWhatsApp = () => {
        const cleanPhone = phoneNumber.replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/55${cleanPhone}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        setIsOpen(false);
    };

    if (!phoneNumber) {
        return <span style={{ color: '#999' }}>-</span>;
    }

    return (
        <MenuContainer ref={menuRef}>
            <PhoneText onClick={() => setIsOpen(!isOpen)}>
                {phoneNumber}
            </PhoneText>
            {isOpen && (
                <>
                    <Overlay onClick={() => setIsOpen(false)} />
                    <Menu>
                        <MenuItem onClick={handleCopy}>
                            <CopyIcon />
                            Copiar número
                        </MenuItem>
                        <MenuItem onClick={handleWhatsApp}>
                            <WhatsAppIcon />
                            Abrir no WhatsApp
                        </MenuItem>
                    </Menu>
                </>
            )}
        </MenuContainer>
    );
}
